import React, { useState, useEffect, useRef, useMemo } from 'react'

// Searchable company picker. Each option shows a company icon, the nome fantasia as
// title and the razão social as subtitle. The selection can be cleared in one click
// (inline "x" on the trigger, or the "Sem núcleo" entry at the top of the list).

const companyTitle = (company) => company.nome_fantasia || company.razao_social
const companySubtitle = (company) => (company.nome_fantasia ? company.razao_social : null)

const CompanyOption = ({ company }) => (
    <>
        <span className="material-icons text-gray-400 dark:text-gray-500 flex-shrink-0" style={{ fontSize: '20px' }}>apartment</span>
        <span className="min-w-0 flex-1 text-left">
            <span className="block text-sm text-gray-900 dark:text-gray-100 truncate">{companyTitle(company)}</span>
            {companySubtitle(company) && (
                <span className="block text-xs text-gray-500 dark:text-gray-400 truncate">{companySubtitle(company)}</span>
            )}
        </span>
    </>
)

const CompanySelect = ({
    label,
    helperText,
    value,
    onChange,
    options = [],
    placeholder = 'Sem núcleo',
    clearLabel = 'Sem núcleo',
    disabled = false,
    disabledHint,
}) => {
    const [isOpen, setIsOpen] = useState(false)
    const [search, setSearch] = useState('')
    const containerRef = useRef(null)

    const selected = options.find((company) => company.id === value) || null

    const filtered = useMemo(() => {
        const term = search.trim().toLowerCase()
        if (!term) return options
        return options.filter((company) =>
            (company.nome_fantasia || '').toLowerCase().includes(term) ||
            company.razao_social.toLowerCase().includes(term)
        )
    }, [options, search])

    useEffect(() => {
        if (!isOpen) return
        const handleClickOutside = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) setIsOpen(false)
        }
        const handleEscape = (e) => {
            if (e.key === 'Escape') setIsOpen(false)
        }
        document.addEventListener('mousedown', handleClickOutside)
        document.addEventListener('keydown', handleEscape)
        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
            document.removeEventListener('keydown', handleEscape)
        }
    }, [isOpen])

    const choose = (id) => {
        onChange(id)
        setIsOpen(false)
        setSearch('')
    }

    return (
        <div ref={containerRef} className="relative">
            {label && (
                <label className="block text-sm font-medium mb-2">{label}</label>
            )}

            <div className={`flex items-center border rounded-lg bg-white dark:bg-gray-700 ${
                disabled
                    ? 'border-gray-200 dark:border-gray-600 opacity-60 cursor-not-allowed'
                    : 'border-gray-300 dark:border-gray-600 focus-within:ring-2 focus-within:ring-blue-500'
            }`}>
                <button
                    type="button"
                    disabled={disabled}
                    onClick={() => setIsOpen((open) => !open)}
                    className="flex-1 min-w-0 flex items-center gap-3 px-3 py-2 text-left disabled:cursor-not-allowed focus:outline-none"
                    aria-haspopup="listbox"
                    aria-expanded={isOpen}
                >
                    {selected ? (
                        <CompanyOption company={selected} />
                    ) : (
                        <>
                            <span className="material-icons text-gray-300 dark:text-gray-500 flex-shrink-0" style={{ fontSize: '20px' }}>apartment</span>
                            <span className="flex-1 text-sm text-gray-400 dark:text-gray-500">{placeholder}</span>
                        </>
                    )}
                </button>

                {selected && !disabled && (
                    <button
                        type="button"
                        onClick={() => choose(null)}
                        className="p-1 mr-1 rounded text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600 transition"
                        title="Remover associação"
                        aria-label="Remover associação"
                    >
                        <span className="material-icons" style={{ fontSize: '18px' }}>close</span>
                    </button>
                )}
                <span className="material-icons text-gray-400 dark:text-gray-500 pr-2 pointer-events-none" style={{ fontSize: '20px' }}>
                    {isOpen ? 'expand_less' : 'expand_more'}
                </span>
            </div>

            {isOpen && (
                <div className="absolute left-0 right-0 mt-1 z-50 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg overflow-hidden">
                    <div className="p-2 border-b border-gray-100 dark:border-gray-700">
                        <input
                            type="text"
                            autoFocus
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Buscar empresa..."
                            className="w-full px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>
                    <ul role="listbox" className="max-h-64 overflow-y-auto py-1">
                        {selected && (
                            <li>
                                <button
                                    type="button"
                                    onClick={() => choose(null)}
                                    className="w-full flex items-center gap-3 px-3 py-2 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/60 transition"
                                >
                                    <span className="material-icons text-gray-400 dark:text-gray-500 flex-shrink-0" style={{ fontSize: '20px' }}>link_off</span>
                                    {clearLabel}
                                </button>
                            </li>
                        )}
                        {filtered.length === 0 ? (
                            <li className="px-3 py-4 text-center text-sm text-gray-500 dark:text-gray-400">Nenhuma empresa encontrada</li>
                        ) : (
                            filtered.map((company) => (
                                <li key={company.id}>
                                    <button
                                        type="button"
                                        role="option"
                                        aria-selected={company.id === value}
                                        onClick={() => choose(company.id)}
                                        className={`w-full flex items-center gap-3 px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-700/60 transition ${
                                            company.id === value ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                                        }`}
                                    >
                                        <CompanyOption company={company} />
                                        {company.id === value && (
                                            <span className="material-icons text-blue-500 flex-shrink-0" style={{ fontSize: '18px' }}>check</span>
                                        )}
                                    </button>
                                </li>
                            ))
                        )}
                    </ul>
                </div>
            )}

            {disabled && disabledHint ? (
                <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">{disabledHint}</p>
            ) : (
                helperText && <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{helperText}</p>
            )}
        </div>
    )
}

export default CompanySelect
